import type { TonyteActivity } from '../types';
import { normalizeCityKey } from './cities';
import { isEventActivity } from './activityKind';
import { isEventAvailableForDiscovery } from './eventAvailability';
import { getResolvedEventId } from './eventIds';
import { parseActivityDate } from './activityDates';

// =============================================================================
// Promotions — seasonal / editorial event collections ("Halloween in
// Dresden", "Christmas market season"), managed remotely from the admin
// dashboard (Tonyte_website promotionsAdminPanel.js).
//
// Publication model (see firestore.rules `match /promotions/{id}`):
//   promotionDrafts/{id}  admin-only working copy, never readable by users
//   promotions/{id}       the published snapshot an admin explicitly
//                         published; users can read it only while
//                         `published == true`
// Scheduling is data: the published snapshot carries startAt/endAt and the
// client decides eligibility against a live clock, so a promotion activates
// and expires on time even offline and without any new network request.
//
// Promotions only ever reference events by id. Ids are resolved against the
// already-loaded, already-permission-filtered feed pool (useFeed), so a
// promotion can never surface an event the user couldn't otherwise see
// (private, StudiZone-only, other-city, unapproved, cancelled, deleted or
// ended events are simply not resolvable) and adds no per-event reads.
// =============================================================================

export const MAX_FEED_PROMOTIONS = 6;
export const MAX_PROMOTION_EVENTS = 50;
export const PROMOTION_TIME_ZONE = 'Europe/Berlin';

export type PublishedPromotion = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  ctaLabel: string;
  coverImageUrl: string | null;
  startAt: number; // epoch ms
  endAt: number; // epoch ms
  priority: number; // lower = shown first
  cityKeys: string[]; // empty = every city
  eventIds: string[]; // admin order
  version: number;
};

export type FeedPromotion = {
  promotion: PublishedPromotion;
  events: TonyteActivity[];
};

const asString = (value: unknown, max: number) =>
  (typeof value === 'string' ? value.trim().slice(0, max) : '');

function toMillis(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value && typeof value === 'object') {
    const raw = value as { toMillis?: () => number; seconds?: number; nanoseconds?: number };
    if (typeof raw.toMillis === 'function') {
      const ms = raw.toMillis();
      return Number.isFinite(ms) ? ms : null;
    }
    if (typeof raw.seconds === 'number') return raw.seconds * 1000 + Math.floor((raw.nanoseconds || 0) / 1e6);
  }
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
  }
  return null;
}

// Defensive parse of a published promotion (Firestore doc or the persisted
// cache copy). Anything malformed or not explicitly published is dropped —
// a bad remote doc degrades to "not shown", never to a crash.
export function parsePublishedPromotion(id: string, data: Record<string, unknown> | null | undefined): PublishedPromotion | null {
  if (!id || !data || data.published !== true) return null;
  const title = asString(data.title, 80);
  const startAt = toMillis(data.startAt);
  const endAt = toMillis(data.endAt);
  if (!title || startAt === null || endAt === null || endAt <= startAt) return null;
  const eventIds = Array.isArray(data.eventIds)
    ? Array.from(new Set(data.eventIds.filter((v): v is string => typeof v === 'string' && v.length > 0))).slice(0, MAX_PROMOTION_EVENTS)
    : [];
  if (!eventIds.length) return null;
  const cover = asString(data.coverImageUrl, 2000);
  const priority = typeof data.priority === 'number' && Number.isFinite(data.priority) ? data.priority : 100;
  return {
    id,
    title,
    subtitle: asString(data.subtitle, 140),
    description: asString(data.description, 2000),
    ctaLabel: asString(data.ctaLabel, 30),
    coverImageUrl: /^https:\/\//.test(cover) ? cover : null,
    startAt,
    endAt,
    priority,
    cityKeys: Array.isArray(data.cityKeys)
      ? data.cityKeys.filter((v): v is string => typeof v === 'string' && v.length > 0).map(v => normalizeCityKey(v))
      : [],
    eventIds,
    version: typeof data.version === 'number' ? data.version : 1,
  };
}

export function isPromotionLive(promotion: PublishedPromotion, now: number): boolean {
  return now >= promotion.startAt && now < promotion.endAt;
}

export function promotionTargetsCity(promotion: PublishedPromotion, cityKey: string | null | undefined): boolean {
  if (!promotion.cityKeys.length) return true;
  if (!cityKey) return false;
  return promotion.cityKeys.includes(normalizeCityKey(cityKey));
}

// The promotion's events, in the admin's order, limited to what this user can
// actually see right now in the selected city. The pool is useFeed's
// permission-filtered list; `isEventAvailableForDiscovery` re-checks with a
// live clock because the pool can outlive an event (it ends, is cancelled…).
export function resolvePromotionEvents(
  promotion: PublishedPromotion,
  pool: TonyteActivity[],
  options: { now: number; cityKey?: string | null },
): TonyteActivity[] {
  const byId = new Map<string, TonyteActivity>();
  pool.forEach(activity => {
    if (!byId.has(activity.id)) byId.set(activity.id, activity);
    const resolved = getResolvedEventId(activity);
    if (resolved && !byId.has(resolved)) byId.set(resolved, activity);
  });
  const now = new Date(options.now);
  const cityKey = options.cityKey ? normalizeCityKey(options.cityKey) : '';
  const seen = new Set<string>();
  const events: TonyteActivity[] = [];
  promotion.eventIds.forEach(id => {
    const activity = byId.get(id);
    if (!activity || seen.has(activity.id)) return;
    seen.add(activity.id);
    if (!isEventActivity(activity) || !isEventAvailableForDiscovery(activity, now)) return;
    if (cityKey && normalizeCityKey(activity.cityKey || activity.city) !== cityKey) return;
    events.push(activity);
  });
  return events;
}

export function comparePromotions(a: PublishedPromotion, b: PublishedPromotion): number {
  return a.priority - b.priority || a.startAt - b.startAt || a.title.localeCompare(b.title) || a.id.localeCompare(b.id);
}

// What the Events feed shows: live, city-targeted promotions that still have
// at least one eligible event — an empty collection is hidden, never shown
// as a blank card.
export function selectFeedPromotions(
  promotions: PublishedPromotion[],
  pool: TonyteActivity[],
  options: { now: number; cityKey?: string | null; limit?: number },
): FeedPromotion[] {
  return promotions
    .filter(p => isPromotionLive(p, options.now) && promotionTargetsCity(p, options.cityKey))
    .sort(comparePromotions)
    .map(promotion => ({ promotion, events: resolvePromotionEvents(promotion, pool, options) }))
    .filter(entry => entry.events.length > 0)
    .slice(0, options.limit ?? MAX_FEED_PROMOTIONS);
}

// The next instant at which any promotion starts or ends — the client
// re-evaluates exactly then instead of polling.
export function nextPromotionBoundary(promotions: PublishedPromotion[], now: number): number | null {
  let next: number | null = null;
  promotions.forEach(p => {
    [p.startAt, p.endAt].forEach(boundary => {
      if (boundary > now && (next === null || boundary < next)) next = boundary;
    });
  });
  return next;
}

// Cached copies are trimmed to what can still become visible, so a
// promotion cached before going offline can never outlive its endAt.
export function dropExpiredPromotions(promotions: PublishedPromotion[], now: number): PublishedPromotion[] {
  return promotions.filter(p => p.endAt > now);
}

function formatDay(ms: number, locale: string, withYear: boolean): string {
  const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}) };
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: PROMOTION_TIME_ZONE }).format(new Date(ms));
  } catch {
    try {
      return new Date(ms).toLocaleDateString(locale, options);
    } catch {
      return new Date(ms).toDateString();
    }
  }
}

// "20 Oct – 2 Nov" (Europe/Berlin). The end is exclusive, so a promotion
// ending exactly at midnight is labelled with the previous day.
export function formatPromotionDateRange(promotion: Pick<PublishedPromotion, 'startAt' | 'endAt'>, locale = 'en', now = Date.now()): string {
  const lastMoment = promotion.endAt - 1;
  const crossesYear = new Date(promotion.startAt).getFullYear() !== new Date(lastMoment).getFullYear()
    || new Date(lastMoment).getFullYear() !== new Date(now).getFullYear();
  const start = formatDay(promotion.startAt, locale, crossesYear);
  const end = formatDay(lastMoment, locale, crossesYear);
  return start === end ? start : `${start} – ${end}`;
}

// The next moment one of the shown events ends. Each event drops out of its
// collection then, and a collection whose last event ended disappears — the
// feed re-evaluates exactly at that instant instead of waiting for endAt.
export function nextEventEnd(entries: FeedPromotion[], now: number): number | null {
  let next: number | null = null;
  entries.forEach(entry => entry.events.forEach(event => {
    const end = parseActivityDate(event).endAt?.getTime();
    if (typeof end === 'number' && end >= now && (next === null || end < next)) next = end;
  }));
  return next;
}
